import { Input, InputProps } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import clsx from "clsx"
import React, { ChangeEvent } from "react"
import {
  Control,
  Controller,
  UseControllerProps,
  useFormContext,
} from "react-hook-form"
import { Combobox } from "./combobox"

export type OptionType = {
  label: string
  value: string
  isDisabled?: boolean
  hideErrors?: boolean
}

export interface RhfFieldProps extends InputProps, UseControllerProps {
  name: string
  control: Control<any>
  label?: string
  defaultValue?: string
  hidden?: boolean
  showError?: boolean
  rightLabelContent?: React.ReactNode
  options?: OptionType[]
  afterChange?: () => void
  as?: "input" | "select"
}

export const RhfField = ({
  name,
  control,
  label,
  showError = true,
  hidden,
  rightLabelContent,
  as = "input",
  placeholder,
  options,
  disabled,
  afterChange,
  ...props
}: RhfFieldProps) => {
  const formContext = useFormContext()
  const {
    formState: { errors },
  } = formContext
  const error = errors[name]?.message

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => {
        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          onChange(e)

          if (afterChange) {
            afterChange()
          }

          return props
        }

        return (
          <div
            className={clsx("w-full text-left", {
              hidden: hidden,
            })}
            data-test={`${name}-field`}
          >
            <div className="flex justify-between">
              <Label>{label}</Label>
              {!!rightLabelContent && (
                <div className="leading-none">{rightLabelContent}</div>
              )}
            </div>
            {as === "select" ? (
              <Combobox
                value={value}
                options={options || []}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
              />
            ) : (
              <Input
                value={value}
                onChange={handleChange}
                onBlur={onBlur}
                name={name}
                placeholder={placeholder}
                disabled={disabled}
                {...props}
              />
            )}

            {!!error && showError && (
              <div className="mt-2">
                <div className="text-red-500 text-xs">
                  {typeof error === "string" ? error : "Invalid input"}
                </div>
              </div>
            )}
          </div>
        )
      }}
    />
  )
}
